// PeopleModal — the "who attended" page for an album. Shows everyone who
// opted in to auto-tagging with their face icon. Tap to select one or more
// people and build a combined virtual album (photos containing ALL of them).
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X as XIcon, Users, Check, ScanFace } from "lucide-react";
import { FaceAvatar, colorForName } from "./GuestFacePills";

export default function PeopleModal({ open, onClose, registrations, activeGuestIds, faceMode, onModeChange, onToggleGuest, onClear }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const selected = new Set(activeGuestIds || []);

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 80, background: "rgba(6,4,14,0.85)", backdropFilter: "blur(10px)" }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="people-modal"
    >
      <div
        className="w-full max-w-lg rounded-3xl p-6 max-h-[85vh] overflow-y-auto"
        style={{ background: "#15131F", border: "1px solid rgba(127,75,255,0.35)", boxShadow: "0 30px 80px -20px rgba(0,0,0,0.9)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-1">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center"
               style={{ background: "rgba(34,226,255,0.12)", border: "1px solid rgba(34,226,255,0.45)" }}>
            <Users size={20} color="#22E2FF" />
          </div>
          <div className="flex-1">
            <div className="text-lg font-semibold tracking-tight">People at this event</div>
            <div className="text-xs" style={{ color: "rgba(255,255,255,0.55)" }}>
              {registrations.length} {registrations.length === 1 ? "person" : "people"} opted in to auto-tagging
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)" }}
            aria-label="Close"
            data-testid="people-modal-close"
          >
            <XIcon size={16} />
          </button>
        </div>

        <div className="text-[12px] rounded-xl px-3 py-2 my-4 flex items-start gap-2"
             style={{ background: "rgba(127,75,255,0.08)", border: "1px solid rgba(127,75,255,0.3)", color: "rgba(255,255,255,0.7)" }}>
          <ScanFace size={13} className="mt-0.5 shrink-0" color="#C9B6FF" />
          <span>
            Tap faces to build a virtual album. Pick two or more — like Shawna + Bill — to see
            only the photos they&rsquo;re in <em>together</em>.
          </span>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="people-grid">
          {registrations.map((r) => {
            const active = selected.has(r.guest_id);
            const color = colorForName(r.name);
            return (
              <button
                key={r.guest_id}
                type="button"
                onClick={() => onToggleGuest?.(r)}
                className="relative flex flex-col items-center gap-2 rounded-2xl px-2 py-3 transition"
                style={{
                  background: active ? `${color}22` : "rgba(255,255,255,0.03)",
                  border: `1px solid ${active ? color : "rgba(255,255,255,0.10)"}`,
                }}
                data-testid={`people-card-${r.guest_id}`}
              >
                {active && (
                  <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center"
                        style={{ background: color }}>
                    <Check size={12} color="#0B0713" strokeWidth={3} />
                  </span>
                )}
                <FaceAvatar reg={r} size={56} active={active} />
                <span className="text-xs font-medium truncate w-full text-center">{r.name}</span>
              </button>
            );
          })}
        </div>

        <div className="flex gap-2 mt-5 items-center flex-wrap">
          {selected.size >= 2 && (
            <div
              className="inline-flex rounded-full overflow-hidden text-[11px] font-medium"
              style={{ border: "1px solid rgba(255,255,255,0.18)" }}
              data-testid="people-modal-mode-toggle"
            >
              <button
                type="button"
                onClick={() => onModeChange?.("and")}
                className="px-3 py-2"
                style={{
                  background: faceMode !== "or" ? "rgba(127,75,255,0.35)" : "transparent",
                  color: faceMode !== "or" ? "#fff" : "rgba(255,255,255,0.6)",
                }}
                data-testid="people-modal-mode-and"
              >
                Together
              </button>
              <button
                type="button"
                onClick={() => onModeChange?.("or")}
                className="px-3 py-2"
                style={{
                  background: faceMode === "or" ? "rgba(34,226,255,0.30)" : "transparent",
                  color: faceMode === "or" ? "#fff" : "rgba(255,255,255,0.6)",
                }}
                data-testid="people-modal-mode-or"
              >
                Any
              </button>
            </div>
          )}
          {selected.size > 0 && (
            <button
              type="button"
              onClick={onClear}
              className="px-4 py-2.5 rounded-xl text-sm"
              style={{ background: "rgba(255,107,107,0.08)", border: "1px solid rgba(255,107,107,0.35)", color: "#FF9B9B" }}
              data-testid="people-modal-clear"
            >
              Clear
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="flex-1 cta-pill justify-center"
            data-testid="people-modal-apply"
          >
            {selected.size > 0
              ? `Show photos (${selected.size} selected)`
              : "Close"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
